import { useEffect, useState, useCallback } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Progress,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Circle,
  FolderTree,
  Settings,
  Tag,
  Package,
  MemoryStick,
  Box as BoxIcon,
  ArrowRight,
} from 'lucide-react'
import { AssetCatalogShell } from '@/components/assetCatalog/AssetCatalogShell'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useClientContextStore } from '@/store/clientContext'
import { useAssetCatalogPaths } from '@/hooks/useAssetCatalogPaths'
import { apiClient } from '@/lib/api/client'
import { SPEC_OPTION_TYPES } from '@/pages/AssetConfiguration/specOptionTypes'

type StepStatus = 'done' | 'todo' | 'optional'

interface CatalogStep {
  id: string
  title: string
  description: string
  href: string
  count: number | null
  status: StepStatus
  icon: typeof FolderTree
}

async function fetchListCount(path: string): Promise<number> {
  try {
    const { data } = await apiClient.get(path, { params: { per_page: 1 } })
    const metaAll = data?.meta?.all
    if (typeof metaAll === 'number') return metaAll
    const total = data?.meta?.total ?? data?.data?.total
    if (typeof total === 'number') return total
    const rows = Array.isArray(data?.data) ? data.data : data?.data?.data
    if (Array.isArray(rows)) return rows.length
    return 0
  } catch {
    return 0
  }
}

const CORE_STEP_IDS = ['categories', 'functions', 'brands', 'models'] as const

export default function AssetCatalogOverviewPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { path } = useAssetCatalogPaths()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const activeClient = useClientContextStore((s) => s.activeClient)

  const [loading, setLoading] = useState(false)
  const [steps, setSteps] = useState<CatalogStep[]>([])
  const [specFilled, setSpecFilled] = useState(0)
  const [specTotal] = useState(Object.keys(SPEC_OPTION_TYPES).length)

  const load = useCallback(async () => {
    if (!activeClientCode) {
      setSteps([])
      return
    }
    setLoading(true)
    try {
      const [categories, functions, brands, models, assets, ...specCounts] = await Promise.all([
        fetchListCount('/master-data/categories'),
        fetchListCount('/master-data/functions'),
        fetchListCount('/master-data/brands'),
        fetchListCount('/master-data/brand-models'),
        fetchListCount('/inventory-management/assets'),
        ...Object.values(SPEC_OPTION_TYPES).map((t) => fetchListCount(t.apiBase)),
      ])

      const filledSpecs = specCounts.filter((c) => c > 0).length
      setSpecFilled(filledSpecs)

      const coreReady = categories > 0 && brands > 0 && models > 0

      setSteps([
        {
          id: 'categories',
          title: 'Categories',
          description: 'Machine types (POS, Printer, Kiosk…)',
          href: path('categories'),
          count: categories,
          status: categories > 0 ? 'done' : 'todo',
          icon: FolderTree,
        },
        {
          id: 'functions',
          title: 'Functions',
          description: 'Capabilities under each machine type',
          href: path('functions'),
          count: functions,
          status: functions > 0 ? 'done' : 'todo',
          icon: Settings,
        },
        {
          id: 'brands',
          title: 'Brands',
          description: 'Equipment manufacturers linked to categories',
          href: path('brands'),
          count: brands,
          status: brands > 0 ? 'done' : 'todo',
          icon: Tag,
        },
        {
          id: 'models',
          title: 'Models',
          description: 'Specific models under each brand + category',
          href: path('brand-models'),
          count: models,
          status: models > 0 ? 'done' : brands > 0 ? 'todo' : 'todo',
          icon: Package,
        },
        {
          id: 'specs',
          title: 'Spec options',
          description: 'SW Provider, RAM, Disk, MS Office, Processor, CPU, OS',
          href: path('spec/sw-provider'),
          count: filledSpecs,
          status: filledSpecs >= Math.ceil(specTotal / 2) ? 'done' : 'optional',
          icon: MemoryStick,
        },
        {
          id: 'assets',
          title: 'Assets',
          description: coreReady
            ? 'Create and manage store inventory (tag, serial, specs)'
            : 'Requires Categories, Brands, and Models first',
          href: path('assets'),
          count: assets,
          status: assets > 0 ? 'done' : coreReady ? 'todo' : 'optional',
          icon: BoxIcon,
        },
      ])
    } finally {
      setLoading(false)
    }
  }, [activeClientCode, specTotal, path])

  useEffect(() => {
    load()
  }, [load])

  const requiredDone = steps.filter(
    (s) => (CORE_STEP_IDS as readonly string[]).includes(s.id) && s.status === 'done'
  ).length
  const requiredTotal = CORE_STEP_IDS.length
  const progress = requiredTotal ? Math.round((requiredDone / requiredTotal) * 100) : 0
  const nextStep = steps.find((s) => s.status === 'todo')

  return (
    <AssetCatalogShell fillHeight={false}>
      <VStack align="stretch" spacing={4} maxW="1100px">
        {!activeClientCode && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            Loading this client&apos;s catalog context…
          </Alert>
        )}

        {activeClientCode && (
          <>
            <Card>
              <CardBody>
                <HStack justify="space-between" align="start" flexWrap="wrap" spacing={4}>
                  <VStack align="start" spacing={1}>
                    <Heading size="md" color={colors.heading}>
                      {activeClient?.name ?? activeClientCode} catalog
                    </Heading>
                    <Text fontSize="sm" color={colors.textSecondary}>
                      Fill Core (Categories → Functions → Brands → Models), then Spec options for
                      Create Asset dropdowns. Use Assets to create, import, or export inventory.
                      Import catalog CSV per module in that same order (system roots are seeded when
                      the client database is created).
                    </Text>
                  </VStack>
                  {nextStep && (
                    <Button
                      colorScheme="blue"
                      rightIcon={<ArrowRight size={16} />}
                      onClick={() => navigate(nextStep.href)}
                    >
                      Continue: {nextStep.title}
                    </Button>
                  )}
                </HStack>
                <Box mt={4}>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      Core setup
                    </Text>
                    <Text fontSize="sm" color={colors.textSecondary}>
                      {requiredDone}/{requiredTotal} complete
                    </Text>
                  </HStack>
                  <Progress value={progress} size="sm" borderRadius="full" colorScheme="blue" />
                </Box>
              </CardBody>
            </Card>

            {loading ? (
              <HStack justify="center" py={10}>
                <Spinner />
                <Text color={colors.textSecondary}>Loading catalog status…</Text>
              </HStack>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                {steps.map((step) => {
                  const Icon = step.icon
                  const isDone = step.status === 'done'
                  return (
                    <Card
                      key={step.id}
                      cursor="pointer"
                      onClick={() => navigate(step.href)}
                      _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
                      borderWidth="1px"
                      borderColor={colors.border}
                    >
                      <CardBody>
                        <HStack align="start" spacing={3}>
                          <Box
                            p={2}
                            borderRadius="md"
                            bg={isDone ? 'green.50' : 'gray.50'}
                            color={isDone ? 'green.600' : 'gray.500'}
                            _dark={{
                              bg: isDone ? 'green.900' : 'gray.700',
                              color: isDone ? 'green.200' : 'gray.400',
                            }}
                          >
                            <Icon size={18} />
                          </Box>
                          <VStack align="start" spacing={1} flex="1" minW={0}>
                            <HStack justify="space-between" w="full">
                              <Text fontWeight="semibold" color={colors.heading}>
                                {step.title}
                              </Text>
                              {isDone ? (
                                <CheckCircle2 size={16} color="var(--chakra-colors-green-500)" />
                              ) : (
                                <Circle size={16} color="var(--chakra-colors-gray-400)" />
                              )}
                            </HStack>
                            <Text fontSize="sm" color={colors.textSecondary} noOfLines={2}>
                              {step.description}
                            </Text>
                            <HStack pt={1}>
                              <Badge
                                colorScheme={
                                  isDone ? 'green' : step.status === 'optional' ? 'purple' : 'orange'
                                }
                              >
                                {step.id === 'specs'
                                  ? `${specFilled}/${specTotal} types filled`
                                  : `${step.count ?? 0} items`}
                              </Badge>
                              {step.status === 'optional' && !isDone && (
                                <Badge variant="outline">Optional</Badge>
                              )}
                            </HStack>
                          </VStack>
                        </HStack>
                      </CardBody>
                    </Card>
                  )
                })}
              </SimpleGrid>
            )}

            <Card variant="outline">
              <CardBody>
                <Text fontSize="sm" color={colors.textSecondary}>
                  <Text as="span" fontWeight="semibold" color={colors.heading}>
                    Tip:{' '}
                  </Text>
                  This catalog is locked to the client you opened from Organization → Clients. Use
                  the left menu for Core, Spec options, and Assets. Support Admin consumes this data
                  when attaching assets to tickets.
                </Text>
              </CardBody>
            </Card>
          </>
        )}
      </VStack>
    </AssetCatalogShell>
  )
}
